import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import api from "@/shared/api";
import { logDebug } from "@/shared/utils/logger.js";

/**
 * Quote Link Context - Manages quote data and actions state
 * Provides centralized state management for quote link pages
 */

// Initial state
const initialState = {
  quote: null,
  actions: [],
  loading: false,
  error: null,
  lastUpdated: null,
  loadingQuoteId: null,
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: "SET_LOADING",
  SET_QUOTE: "SET_QUOTE",
  SET_ACTIONS: "SET_ACTIONS",
  SET_ERROR: "SET_ERROR",
  UPDATE_QUOTE: "UPDATE_QUOTE",
  ADD_ACTION: "ADD_ACTION",
  CLEAR_ERROR: "CLEAR_ERROR",
  RESET_STATE: "RESET_STATE",
  SET_LOADING_QUOTE_ID: "SET_LOADING_QUOTE_ID",
};

// Reducer function
const quoteLinkReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error,
      };

    case ACTION_TYPES.SET_QUOTE:
      return {
        ...state,
        quote: action.payload,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString(),
      };

    case ACTION_TYPES.SET_ACTIONS:
      return {
        ...state,
        actions: action.payload,
        loading: false,
        error: null,
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };

    case ACTION_TYPES.UPDATE_QUOTE:
      return {
        ...state,
        quote: { ...state.quote, ...action.payload },
        lastUpdated: new Date().toISOString(),
      };

    case ACTION_TYPES.ADD_ACTION:
      return {
        ...state,
        actions: [action.payload, ...state.actions],
      };

    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ACTION_TYPES.RESET_STATE:
      return initialState;

    case ACTION_TYPES.SET_LOADING_QUOTE_ID:
      return {
        ...state,
        loadingQuoteId: action.payload,
      };

    default:
      return state;
  }
};

// Create context
const QuoteLinkContext = createContext();

// Simple in-memory throttle to avoid spamming backend on rapid re-renders
const recentRequests = new Map();

// Provider component
export const QuoteLinkProvider = ({ children }) => {
  const [state, dispatch] = useReducer(quoteLinkReducer, initialState);

  // Actions
  const actions = {
    setLoading: (loading) => {
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading });
    },

    setQuote: (quote) => {
      dispatch({ type: ACTION_TYPES.SET_QUOTE, payload: quote });
    },

    setActions: (actions) => {
      dispatch({ type: ACTION_TYPES.SET_ACTIONS, payload: actions });
    },

    setError: (error) => {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
    },

    updateQuote: (updates) => {
      dispatch({ type: ACTION_TYPES.UPDATE_QUOTE, payload: updates });
    },

    addAction: (action) => {
      dispatch({ type: ACTION_TYPES.ADD_ACTION, payload: action });
    },

    clearError: () => {
      dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
    },

    resetState: () => {
      dispatch({ type: ACTION_TYPES.RESET_STATE });
    },

    setLoadingQuoteId: (loadingQuoteId) => {
      dispatch({
        type: ACTION_TYPES.SET_LOADING_QUOTE_ID,
        payload: loadingQuoteId,
      });
    },
  };

  const value = {
    ...state,
    ...actions,
  };

  return (
    <QuoteLinkContext.Provider value={value}>
      {children}
    </QuoteLinkContext.Provider>
  );
};

// Hook to use the context
export const useQuoteLinkContext = () => {
  const context = useContext(QuoteLinkContext);
  if (!context) {
    throw new Error(
      "useQuoteLinkContext must be used within a QuoteLinkProvider"
    );
  }
  return context;
};

// Hook for quote link pages
export const useQuoteLink = (tokenId) => {
  const context = useQuoteLinkContext();
  const {
    quote,
    loading,
    loadingQuoteId,
    // action methods are provided at the top level of context
    setLoading,
    setError,
    setLoadingQuoteId,
    setQuote,
    setActions,
  } = context;

  // Normalize phone number to South African format
  const normalizePhone = useCallback((phone) => {
    if (!phone) return phone;

    // Remove any spaces, dashes, or parentheses
    const cleanPhone = phone.replace(/[\s\-()]/g, "");

    // If it starts with 0, replace with +27
    if (cleanPhone.startsWith("0")) {
      return "+27" + cleanPhone.substring(1);
    }

    // If it doesn't start with +27, add it
    if (!cleanPhone.startsWith("+27")) {
      return "+27" + cleanPhone;
    }

    return cleanPhone;
  }, []);

  const loadQuote = useCallback(
    async (tokenId, phone = "", retryCount = 0) => {
      // Prevent multiple simultaneous requests for the same quote
      const requestKey = `${tokenId}-${phone}`;
      if (loading && loadingQuoteId === requestKey) {
        logDebug("QuoteLinkContext: duplicate request skipped", {
          requestKey,
        });
        return;
      }

      // Throttle identical requests within 3 seconds
      const now = Date.now();
      const last = recentRequests.get(requestKey) || 0;
      if (now - last < 3000) {
        logDebug("QuoteLinkContext: throttled duplicate request", {
          requestKey,
          elapsedMs: now - last,
        });
        return;
      }
      recentRequests.set(requestKey, now);

      try {
        setLoading(true);
        setError(null);
        setLoadingQuoteId(requestKey);

        // Build request with phone parameter only if provided
        const normalizedPhone = phone ? normalizePhone(phone) : "";

        logDebug("QuoteLinkContext: loading quote", {
          tokenId,
          phone: normalizedPhone,
          retryCount,
        });

        // Use shared API client and correct base path (public endpoint → skip auth)
        const response = await api.get(
          `/quotes/link/${tokenId}`,
          normalizedPhone
            ? {
                params: { phone: normalizedPhone },
                skipAuth: true,
                skipAuthRedirect: true,
              }
            : { skipAuth: true, skipAuthRedirect: true }
        );
        const data = response.data || response;

        if (data.success) {
          setQuote(data.quote);
          if (data.actions) {
            setActions(data.actions);
          }
        } else {
          setError(data.error || "Failed to load quote");
        }
      } catch (error) {
        console.error("Error loading quote:", error);
        if (
          error?.message &&
          error.message.toLowerCase().includes("phone number is required")
        ) {
          setError("phone_required");
        } else if (error?.message) {
          const msg = error.message.toLowerCase();
          if (
            msg.includes("too many requests") ||
            msg.includes("429") ||
            msg.includes("rate limit")
          ) {
            setError("rate_limited");
          } else {
            setError(error.message || "Failed to load quote");
          }
        } else {
          setError("Failed to load quote");
        }
      } finally {
        setLoading(false);
        setLoadingQuoteId(null);
      }
    },
    [
      loading,
      loadingQuoteId,
      setLoading,
      setError,
      setLoadingQuoteId,
      setQuote,
      setActions,
      normalizePhone,
    ]
  );

  const refreshQuote = useCallback(() => {
    if (tokenId) {
      loadQuote(tokenId);
    }
  }, [tokenId, loadQuote]);

  // Auto-load quote when tokenId changes
  useEffect(() => {
    if (tokenId && !quote) {
      loadQuote(tokenId);
    }
  }, [tokenId, quote, loadQuote]);

  return {
    ...context,
    loadQuote,
    refreshQuote,
  };
};

// Default export for backward compatibility
export default QuoteLinkContext;
